import React from 'react';
import BotAvatar from '../bots/BotAvatar';
import { useTranslation } from 'react-i18next';

/**
 * Member list shown while the user is typing an @mention. Bots come first:
 * mentioning one asks it to answer in the thread. Bots are passed as
 * { bot_id, full_name, team, avatar_color }; the team (of a team bot) tells same-named bots apart.

 *
 * Presentational only: the composer owns caret handling and the query string,
 * so this component stays testable and reusable.
 */
const MentionPicker = ({ members = [], bots = [], query = '', onSelect, onClose }) => {
  const { t } = useTranslation();
  const needle = query.trim().toLowerCase();
  const matches = [...bots, ...members]
    .filter((m) => (m.full_name || m.email || '').toLowerCase().includes(needle))
    .slice(0, 8);

  if (matches.length === 0) {
    return (
      <div className="absolute bottom-full mb-1 left-0 z-50 w-56 rounded-md border border-white/10 bg-neutral-900 p-2 text-xs text-muted-foreground">
        {t('unread.mention_no_members')}
      </div>
    );
  }

  return (
    <div
      className="absolute bottom-full mb-1 left-0 z-50 w-56 rounded-md border border-white/10 bg-neutral-900 py-1 shadow-lg"
      onMouseLeave={onClose}
    >
      {matches.map((m) => (
        <button
          key={m.bot_id || m.user_id || m.id}
          type="button"
          onClick={() => onSelect(m)}
          className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent"
        >
          {m.bot_id ? <BotAvatar bot={{ id: m.bot_id, avatar_color: m.avatar_color }} size={20} /> : (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-[10px] font-medium text-foreground">
              {(m.full_name || m.email || '?').charAt(0).toUpperCase()}
            </span>
          )}
          <span className="truncate flex-1">
            {m.full_name || m.email}
            {m.team && <span className="text-muted-foreground"> · {m.team}</span>}
          </span>
          {m.bot_id && (
            <span className="text-[9px] font-semibold uppercase tracking-wide rounded px-1 bg-amber-500/15 text-amber-500">
              {t('bots.bot_badge')}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default MentionPicker;
